import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent,
  TextField, Button, FormControl, InputLabel,
  Select, MenuItem, FormHelperText, Grid,
  CircularProgress, Alert, Chip, Stack,
  CardHeader, Divider, Rating,
  Avatar, Paper, InputAdornment
} from '@mui/material';
import { 
  createAudit, 
  getChecklists, 
  getHousekeepingStaff, 
  getTasks 
} from '../services/api';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import {
  Assignment,
  Person,
  Task,
  Grade,
  Description,
  Cancel,
  Save,
  Checklist as ChecklistIcon,
  TrendingUp
} from '@mui/icons-material';

export const AuditCreate = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    task: '',
    inspectedBy: '',
    checklistResults: [],
    score: 0,
    remarks: '',
    auditDate: new Date().toISOString().split('T')[0]
  });

  const [staff, setStaff] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedTaskDetails, setSelectedTaskDetails] = useState(null);

  // Load all required data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [staffRes, checklistsRes, tasksRes] = await Promise.all([
          getHousekeepingStaff(),
          getChecklists(),
          getTasks({ status: 'completed', limit: 1000 })
        ]);

        setStaff(staffRes.data || []);
        setChecklists(checklistsRes.data?.items || []);
        setTasks(tasksRes.data.tasks || []);
      } catch (err) {
        console.error('Failed to load data:', err);
        enqueueSnackbar('Failed to load required data', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [enqueueSnackbar]);

  // Auto-calculate score based on checklist results
  useEffect(() => {
    if (form.checklistResults.length > 0) {
      const passedItems = form.checklistResults.filter(item => item.status === 'pass').length;
      const totalItems = form.checklistResults.length;
      const calculatedScore = Math.round((passedItems / totalItems) * 100);
      
      setForm(prev => ({ 
        ...prev, 
        score: isNaN(calculatedScore) ? 0 : calculatedScore 
      }));
    }
  }, [form.checklistResults]);

  // Update selected task details when task is selected
  useEffect(() => {
    if (form.task) {
      const task = tasks.find(t => t._id === form.task);
      setSelectedTaskDetails(task || null);
    } else {
      setSelectedTaskDetails(null);
    }
  }, [form.task, tasks]);

  // Filter tasks to only show completed tasks that haven't been audited
  const getAuditableTasks = useCallback(() => {
    return tasks.filter(task => 
      task.status === 'completed' && 
      !task.auditCompleted
    );
  }, [tasks]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!form.task) {
      newErrors.task = 'Please select a task to audit';
    }
    
    if (!form.inspectedBy) {
      newErrors.inspectedBy = 'Inspector is required';
    }
    
    if (form.checklistResults.length === 0) {
      newErrors.checklistResults = 'At least one checklist item must be completed';
    }
    
    // Validate that all checklist items have results
    const incompleteChecklists = checklists.filter(checklist => 
      !form.checklistResults.some(item => item.checklist === checklist._id)
    );
    
    if (incompleteChecklists.length > 0) {
      newErrors.checklistResults = 'All checklist items must be evaluated';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      enqueueSnackbar('Please fix all form errors before saving', { variant: 'warning' });
      return;
    }

    setSubmitting(true);
    try {
      const auditData = {
        ...form,
        task: form.task,
        remarks: form.remarks.trim(),
        auditDate: new Date().toISOString()
      };

      await createAudit(auditData);
      enqueueSnackbar('🎉 Audit saved successfully!', { variant: 'success' });
      
      // Navigate to audit list after short delay
      setTimeout(() => {
        navigate('/hospital/housekeeping-and-linen-management/housekeeping/audit-list');
      }, 1500);
      
    } catch (err) {
      console.error('Save failed:', err);
      const errorMessage = err.response?.data?.message || 'Failed to save audit. Please try again.';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleChecklistChange = (checklistId, result) => {
    const updatedResults = form.checklistResults.filter(item => item.checklist !== checklistId);
    
    if (result !== '') {
      updatedResults.push({
        checklist: checklistId,
        status: result,
        timestamp: new Date().toISOString()
      });
    }
    
    setForm(prev => ({
      ...prev,
      checklistResults: updatedResults
    }));

    // Clear checklist errors when user makes a selection
    if (errors.checklistResults) {
      setErrors(prev => ({ ...prev, checklistResults: undefined }));
    }
  };

  const getChecklistResult = (checklistId) => {
    const item = form.checklistResults.find(item => item.checklist === checklistId);
    return item ? item.status : '';
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'success';
    if (score >= 70) return 'warning';
    return 'error';
  };

  const getScoreLabel = (score) => {
    if (score >= 90) return 'Excellent';
    if (score >= 80) return 'Good';
    if (score >= 70) return 'Satisfactory';
    return 'Needs Improvement';
  };

  const getCompletionStatus = () => {
    const completed = form.checklistResults.length;
    const total = checklists.length;
    return { completed, total, percentage: Math.round((completed / total) * 100) };
  };

  const handleCancel = () => {
    if (form.checklistResults.length > 0) {
      if (window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
        navigate('/hospital/housekeeping-and-linen-management/housekeeping/audit-list');
      }
    } else {
      navigate('/hospital/housekeeping-and-linen-management/housekeeping/audit-list');
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        p: 8, 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: 500,
        backgroundColor: 'grey.50',
        flexDirection: 'column'
      }}>
        <CircularProgress size={80} thickness={4} sx={{ mb: 3 }} />
        <Typography variant="h3" sx={{ fontSize: '2.2rem', fontWeight: 600, mb: 1 }}>
          Loading Audit Form...
        </Typography>
        <Typography variant="h6" sx={{ fontSize: '1.3rem', color: 'text.secondary' }}>
          Preparing your quality audit workspace
        </Typography>
      </Box>
    );
  }

  const completionStatus = getCompletionStatus();
  const auditableTasks = getAuditableTasks();

  return (
    <Box sx={{ p: 4, backgroundColor: 'grey.50', minHeight: '100vh' }}>
      {/* Header Section */}
      <Card sx={{ 
        mb: 4, 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        borderRadius: 3
      }}>
        <CardContent sx={{ p: 4 }}>
          <Grid container alignItems="center" spacing={3}>
            <Grid item>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <Assignment sx={{ fontSize: 48, color: 'white' }} />
              </Avatar>
            </Grid>
            <Grid item xs>
              <Typography 
                variant="h2" 
                fontWeight={800}
                sx={{ 
                  color: 'white',
                  fontSize: { xs: '2.5rem', md: '3rem' },
                  mb: 1
                }}
              >
                Create Quality Audit
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'white', 
                  opacity: 0.9,
                  fontSize: '1.3rem'
                }}
              >
                Evaluate housekeeping tasks with detailed quality assessment
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={4}>
        {/* Progress Summary Card */}
        <Grid item xs={12}>
          <Card sx={{ 
            borderRadius: 3, 
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Grid container alignItems="center" spacing={4}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Avatar sx={{ backgroundColor: 'primary.main', width: 60, height: 60 }}>
                      <TrendingUp sx={{ fontSize: 32 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" fontWeight={700} sx={{ fontSize: '1.8rem', mb: 1 }}>
                        Audit Progress
                      </Typography>
                      <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
                        Complete all sections to save audit
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end">
                    <Chip 
                      label={`${completionStatus.completed}/${completionStatus.total} Completed`}
                      color={completionStatus.percentage === 100 ? 'success' : 'primary'}
                      variant="filled"
                      sx={{ 
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        py: 2,
                        px: 2
                      }}
                    />
                    {form.score > 0 && (
                      <Chip 
                        label={`Score: ${form.score}% - ${getScoreLabel(form.score)}`}
                        color={getScoreColor(form.score)}
                        sx={{ 
                          fontSize: '1.1rem',
                          fontWeight: 600,
                          py: 2,
                          px: 2
                        }}
                      />
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Alerts */}
        <Grid item xs={12}>
          {staff.length === 0 && !loading && (
            <Alert 
              severity="warning" 
              sx={{ 
                mb: 2,
                fontSize: '1.1rem',
                borderRadius: 3,
                '& .MuiAlert-message': { fontSize: '1.1rem' }
              }}
            >
              ⚠️ No housekeeping staff available. Please add staff members before creating audits.
            </Alert>
          )}
          
          {auditableTasks.length === 0 && tasks.length > 0 && (
            <Alert 
              severity="info"
              sx={{ 
                fontSize: '1.1rem',
                borderRadius: 3,
                '& .MuiAlert-message': { fontSize: '1.1rem' }
              }}
            >
              ℹ️ No completed tasks available for auditing. All tasks have either been audited or are not yet completed.
            </Alert>
          )}
        </Grid>

        {/* Main Form Card */}
        <Grid item xs={12}>
          <Card sx={{ 
            borderRadius: 3, 
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)'
          }}>
            <CardHeader 
              title={
                <Typography variant="h3" fontWeight={700} sx={{ fontSize: '1.8rem' }}>
                  📋 Audit Details
                </Typography>
              }
              subheader={
                <Typography sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
                  Fill in all required information for the quality audit
                </Typography>
              }
              sx={{
                borderBottom: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'grey.50'
              }}
            />
            
            <CardContent sx={{ p: 4 }}>
              <Grid container spacing={4}>
                {/* Task Selection */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!errors.task} required>
                    <InputLabel sx={{ fontSize: '1.1rem' }}>Select Task to Audit</InputLabel>
                    <Select
                      value={form.task}
                      onChange={(e) => setForm({ ...form, task: e.target.value })}
                      label="Select Task to Audit"
                      sx={{ fontSize: '1.1rem' }}
                      startAdornment={
                        <InputAdornment position="start">
                          <Task sx={{ color: 'primary.main', fontSize: 28 }} />
                        </InputAdornment>
                      }
                    >
                      <MenuItem value="" sx={{ fontSize: '1.1rem' }}>
                        <em>Choose a completed task</em>
                      </MenuItem>
                      {auditableTasks.map(task => (
                        <MenuItem key={task._id} value={task._id} sx={{ fontSize: '1.1rem' }}>
                          {task.taskNumber} - Room {task.room?.room_no} 
                          {task.ward?.name && ` - ${task.ward.name}`}
                          {task.assignedTo?.first_name && ` - ${task.assignedTo.first_name} ${task.assignedTo.last_name}`}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.task && (
                      <FormHelperText sx={{ fontSize: '1rem' }}>{errors.task}</FormHelperText>
                    )}
                    <FormHelperText sx={{ fontSize: '1rem' }}>
                      Only completed tasks without existing audits are shown
                    </FormHelperText>
                  </FormControl>
                </Grid>

                {/* Selected Task Details */}
                {selectedTaskDetails && (
                  <Grid item xs={12} md={6}>
                    <Paper 
                      elevation={2} 
                      sx={{ 
                        p: 3, 
                        border: '2px solid',
                        borderColor: 'primary.light',
                        backgroundColor: 'primary.light',
                        borderRadius: 3
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar sx={{ backgroundColor: 'primary.main' }}>
                          <Task />
                        </Avatar>
                        <Typography variant="h5" fontWeight={600} sx={{ fontSize: '1.4rem' }}>
                          Selected Task Details
                        </Typography>
                      </Box>
                      <Stack spacing={2}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                            Task Number:
                          </Typography>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                            {selectedTaskDetails.taskNumber}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                            Room:
                          </Typography>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                            {selectedTaskDetails.room?.room_no}
                          </Typography>
                        </Box>
                        {selectedTaskDetails.ward?.name && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                              Ward:
                            </Typography>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                              {selectedTaskDetails.ward.name}
                            </Typography>
                          </Box>
                        )}
                        {selectedTaskDetails.assignedTo && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                              Staff:
                            </Typography>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                              {selectedTaskDetails.assignedTo.first_name} {selectedTaskDetails.assignedTo.last_name}
                            </Typography>
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                            Status:
                          </Typography>
                          <Chip 
                            label={selectedTaskDetails.status} 
                            color="success"
                            sx={{ 
                              fontSize: '1rem',
                              fontWeight: 600,
                              py: 1
                            }}
                          />
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                )}

                {/* Inspector Selection */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!errors.inspectedBy} required>
                    <InputLabel sx={{ fontSize: '1.1rem' }}>Inspected By</InputLabel>
                    <Select
                      value={form.inspectedBy}
                      onChange={(e) => setForm({ ...form, inspectedBy: e.target.value })}
                      label="Inspected By"
                      sx={{ fontSize: '1.1rem' }}
                      startAdornment={
                        <InputAdornment position="start">
                          <Person sx={{ color: 'primary.main', fontSize: 28 }} />
                        </InputAdornment>
                      }
                    >
                      <MenuItem value="" sx={{ fontSize: '1.1rem' }}>
                        <em>Select Inspector</em>
                      </MenuItem>
                      {staff.map(s => (
                        <MenuItem key={s._id} value={s._id} sx={{ fontSize: '1.1rem' }}>
                          {s.first_name} {s.last_name}
                          {s.role && ` - ${s.role}`}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.inspectedBy && (
                      <FormHelperText sx={{ fontSize: '1rem' }}>{errors.inspectedBy}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                {/* Score Display */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3, backgroundColor: 'background.default' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ backgroundColor: 'secondary.main' }}>
                        <Grade />
                      </Avatar>
                      <Typography variant="h5" fontWeight={600} sx={{ fontSize: '1.4rem' }}>
                        Audit Score
                      </Typography>
                    </Box>
                    
                    <Grid container spacing={3} alignItems="center">
                      <Grid item xs={12} md={8}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                          <Rating 
                            value={form.score / 20} 
                            max={5} 
                            readOnly 
                            size="large"
                            sx={{ fontSize: '2rem' }}
                          />
                          <Box>
                            <Typography 
                              variant="h3" 
                              color={`${getScoreColor(form.score)}.main`}
                              fontWeight={800}
                              sx={{ fontSize: '2.5rem' }}
                            >
                              {form.score}%
                            </Typography>
                            <Typography 
                              variant="h6" 
                              color={`${getScoreColor(form.score)}.main`}
                              sx={{ fontSize: '1.2rem', fontWeight: 600 }}
                            >
                              {getScoreLabel(form.score)}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="body2" sx={{ fontSize: '1rem', color: 'text.secondary' }}>
                          Auto-calculated from checklist results
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="Manual Override"
                          type="number"
                          value={form.score}
                          onChange={(e) => setForm({ ...form, score: Number(e.target.value) })}
                          error={!!errors.score}
                          helperText={errors.score}
                          size="medium"
                          sx={{ fontSize: '1.1rem' }}
                          inputProps={{ 
                            min: 0, 
                            max: 100, 
                            step: 1,
                            style: { fontSize: '1.1rem' }
                          }}
                          InputLabelProps={{ sx: { fontSize: '1.1rem' } }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Checklist Items */}
                {checklists.length > 0 && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                        <Avatar sx={{ backgroundColor: 'info.main' }}>
                          <ChecklistIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="h3" fontWeight={700} sx={{ fontSize: '1.8rem', mb: 0.5 }}>
                            Checklist Evaluation
                          </Typography>
                          <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
                            Evaluate each item below. Your selections will automatically calculate the audit score.
                          </Typography>
                        </Box>
                      </Box>
                      
                      {errors.checklistResults && (
                        <Alert 
                          severity="error" 
                          sx={{ 
                            mb: 3,
                            fontSize: '1.1rem',
                            '& .MuiAlert-message': { fontSize: '1.1rem' }
                          }}
                        >
                          {errors.checklistResults}
                        </Alert>
                      )}
                      
                      <Grid container spacing={3}>
                        {checklists.map((checklist, index) => (
                          <Grid item xs={12} md={6} key={checklist._id}>
                            <Card 
                              variant="outlined" 
                              sx={{ 
                                borderRadius: 3,
                                border: '2px solid',
                                borderColor: getChecklistResult(checklist._id) === 'pass' ? 'success.light' : 
                                           getChecklistResult(checklist._id) === 'fail' ? 'error.light' : 'divider',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: 3
                                }
                              }}
                            >
                              <CardContent sx={{ p: 3 }}>
                                <Typography 
                                  variant="h6" 
                                  fontWeight={600} 
                                  sx={{ fontSize: '1.3rem', mb: 2 }}
                                >
                                  {index + 1}. {checklist.name || checklist.title}
                                </Typography>
                                
                                {(checklist.description || checklist.instructions) && (
                                  <Typography 
                                    variant="body1" 
                                    sx={{ 
                                      fontSize: '1.1rem', 
                                      color: 'text.secondary', 
                                      mb: 3,
                                      lineHeight: 1.6
                                    }}
                                  >
                                    {checklist.description || checklist.instructions}
                                  </Typography>
                                )}
                                
                                <FormControl fullWidth size="medium">
                                  <InputLabel sx={{ fontSize: '1.1rem' }}>Evaluation Result</InputLabel>
                                  <Select
                                    value={getChecklistResult(checklist._id)}
                                    onChange={(e) => handleChecklistChange(checklist._id, e.target.value)}
                                    label="Evaluation Result"
                                    sx={{ fontSize: '1.1rem' }}
                                  >
                                    <MenuItem value="pass" sx={{ fontSize: '1.1rem' }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: 'success.main' }} />
                                        <Typography sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                          Pass - Meets Standards
                                        </Typography>
                                      </Box>
                                    </MenuItem>
                                    <MenuItem value="fail" sx={{ fontSize: '1.1rem' }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: 'error.main' }} />
                                        <Typography sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                          Fail - Needs Improvement
                                        </Typography>
                                      </Box>
                                    </MenuItem>
                                    <MenuItem value="na" sx={{ fontSize: '1.1rem' }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: 'grey.500' }} />
                                        <Typography sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                          Not Applicable
                                        </Typography>
                                      </Box>
                                    </MenuItem>
                                  </Select>
                                </FormControl>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Paper>
                  </Grid>
                )}

                {/* Remarks */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <Avatar sx={{ backgroundColor: 'warning.main' }}>
                        <Description />
                      </Avatar>
                      <Typography variant="h3" fontWeight={700} sx={{ fontSize: '1.8rem' }}>
                        Audit Remarks & Observations
                      </Typography>
                    </Box>
                    
                    <TextField
                      label="Detailed feedback and observations"
                      multiline
                      rows={5}
                      value={form.remarks}
                      onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                      fullWidth
                      placeholder="Document any specific observations, issues found, commendations, or areas for improvement..."
                      helperText="Provide comprehensive feedback about the audit findings to help improve quality standards"
                      InputProps={{
                        sx: { fontSize: '1.1rem' }
                      }}
                      InputLabelProps={{ 
                        sx: { fontSize: '1.1rem' }
                      }}
                      FormHelperTextProps={{
                        sx: { fontSize: '1rem' }
                      }}
                    />
                  </Paper>
                </Grid>

                {/* Action Buttons */}
                <Grid item xs={12}>
                  <Paper 
                    sx={{ 
                      p: 4, 
                      backgroundColor: 'grey.50',
                      border: '2px solid',
                      borderColor: 'divider',
                      borderRadius: 3
                    }}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 3, 
                      justifyContent: 'flex-end',
                      alignItems: 'center'
                    }}>
                      <Button
                        onClick={handleCancel}
                        disabled={submitting}
                        size="large"
                        startIcon={<Cancel sx={{ fontSize: 28 }} />}
                        sx={{ 
                          fontSize: '1.2rem',
                          px: 4,
                          py: 1.5,
                          fontWeight: 600
                        }}
                      >
                        Cancel
                      </Button>

                      <Button 
                        variant="contained" 
                        onClick={handleSave}
                        disabled={submitting || staff.length === 0 || auditableTasks.length === 0}
                        startIcon={
                          submitting ? 
                            <CircularProgress size={24} color="inherit" /> : 
                            <Save sx={{ fontSize: 28 }} />
                        }
                        size="large"
                        sx={{ 
                          fontSize: '1.2rem',
                          fontWeight: 600,
                          px: 5,
                          py: 1.5,
                          minWidth: 200,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)'
                          },
                          transition: 'all 0.3s ease'
                        }}
                      >
                        {submitting ? 'Saving Audit...' : 'Save Quality Audit'}
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};